import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'

import PageContainer from '@/components/ui/containers/PageContainer'
import ConsultationIcon from '@/components/ui/icons/Consultation'
import { OfflineStatusIndicator } from '@/shared/offline'

import StartLocalConsultationDialog from '@/pages/home/containers/StartLocalConsultationDialog'
import Pagination from '@/components/ui/pagination/Pagination'

import { OfflineConsultationsDesktop } from './OfflineConsultationsDesktop'
import { OfflineConsultationsMobile } from './OfflineConsultationsMobile'
import { useOfflineConsultationsLoader } from '../model/useOfflineConsultationsLoader'
import { OfflineInitializing } from '@/shared/offline'
import { Searchbar } from './Searchbar'
import { useConsultations } from '../model/useConsultations'

export function OfflineConsultationsPage() {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  const [openCreateConsultationDialog, setOpenCreateConsultationDialog] = useState(false)

  const { consultations, loading, isOfflineDatabaseReady } = useOfflineConsultationsLoader()
  const { consultations: paginatedConsultations, itemsPerPage, page, rowsPerPageOptions, setFilter, totalConsultations } = useConsultations(consultations)

  const handleChangePage = (newPage: number) => {
    setFilter((prevFilter) => ({ ...prevFilter, page: newPage }))
  }

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    setFilter((prevFilter) => ({ ...prevFilter, itemsPerPage: newItemsPerPage, page: 1 }))
  }

  if (!isOfflineDatabaseReady) {
    return <OfflineInitializing />
  }

  return (
    <PageContainer>
      <OfflineStatusIndicator />
      <Grid container spacing={2}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                disableElevation
                startIcon={<ConsultationIcon htmlColor="#FFF" />}
                onClick={() => setOpenCreateConsultationDialog(true)}
                variant="contained"
              >
                <Trans>New consultation</Trans>
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Searchbar setFilter={setFilter} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {isMobile ? (
            <Box>
              {totalConsultations > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                  <Pagination
                    color="primary"
                    itemsPerPageOptions={rowsPerPageOptions}
                    shape="rounded"
                    page={page}
                    onPageChange={handleChangePage}
                    onItemsPerPageChange={handleChangeItemsPerPage}
                    totalItems={totalConsultations}
                  />
                </Box>
              )}
              <OfflineConsultationsMobile
                consultations={paginatedConsultations}
                loading={loading}
                onOpen={(consultation) => navigate(`/offline/consultations/${consultation._offlineId || consultation._id}`)}
              />
            </Box>
          ) : (
            <OfflineConsultationsDesktop
              consultations={paginatedConsultations}
              loading={loading}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeItemsPerPage}
              page={page}
              rowsPerPage={itemsPerPage}
              totalCount={totalConsultations}
            />
          )}
        </Grid>
      </Grid>
      {openCreateConsultationDialog && (
        <StartLocalConsultationDialog open={openCreateConsultationDialog} onClose={() => setOpenCreateConsultationDialog(false)} />
      )}
    </PageContainer>
  )
}
